import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Award, Flame, Zap, Trophy, Lock, CheckCircle2, Star, Copy, Pencil,
  Database, Fingerprint, Calculator, Filter, Terminal, TrendingUp, Briefcase, ShieldCheck,
  Bell, Mic, Swords, Mail, Users, Gift, Snowflake, BellRing, Video,
} from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import PortfolioAnalyticsCard from "@/components/PortfolioAnalyticsCard";
import { isPushSubscribed, subscribeToPush, unsubscribeFromPush } from "@/lib/pushNotifications";

const ICON_MAP = {
  footprints: Award, flame: Flame, target: CheckCircle2, "trending-up": TrendingUp, zap: Zap,
  award: Award, trophy: Trophy, medal: Trophy, database: Database, fingerprint: Fingerprint,
  calculator: Calculator, filter: Filter, terminal: Terminal, briefcase: Briefcase,
};

const NOTIFICATION_TYPE_META = {
  achievement: { label: "Achievements & Badges", desc: "Badge unlocks, certificate ready.", icon: Award },
  challenge: { label: "Challenge Unlocks", desc: "Daily challenge completions, next day unlocked, submission/simulator reviews.", icon: CheckCircle2 },
  interview: { label: "Interview Feedback", desc: "AI mock interview results are ready.", icon: Mic },
  recruiter: { label: "Recruiter Activity", desc: "A recruiter viewed your profile or sent you a message.", icon: Briefcase },
  duel: { label: "Duel Results", desc: "Live 1-on-1 duel outcomes.", icon: Swords },
  reminder: { label: "Daily Streak Reminders", desc: "A nudge when you haven't completed today's challenge yet.", icon: Flame },
  friend: { label: "Friend Requests", desc: "New friend requests and acceptances.", icon: Users },
  meeting: { label: "Meeting Invites", desc: "When someone invites you to a live meeting or class.", icon: Video },
  referral: { label: "Referral Updates", desc: "New referrals and referral bonus payouts.", icon: Gift },
  system: { label: "Platform Announcements", desc: "Admin broadcasts and account updates.", icon: Bell },
};

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingBio, setEditingBio] = useState(false);
  const [bioText, setBioText] = useState("");
  const [saving, setSaving] = useState(false);
  const [privacy, setPrivacy] = useState(null);
  const [notifSettings, setNotifSettings] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    api.get("/users/me/profile").then((res) => {
      setProfile(res.data);
      setBioText(res.data.bio || "");
    }).finally(() => setLoading(false));
    api.get("/users/me/privacy").then((res) => setPrivacy(res.data));
    api.get("/notifications/settings").then((res) => setNotifSettings(res.data));
    isPushSubscribed().then(setPushEnabled).catch(() => setPushEnabled(false));
  }, []);

  const togglePush = async (enabled) => {
    setPushBusy(true);
    try {
      if (enabled) {
        await subscribeToPush();
        toast.success("Browser push notifications enabled");
      } else {
        await unsubscribeFromPush();
        toast.success("Browser push notifications disabled");
      }
      setPushEnabled(enabled);
    } catch (e) {
      toast.error(e.message || "Could not update push notification settings");
    } finally {
      setPushBusy(false);
    }
  };

  const updatePrivacy = async (key, value) => {
    setPrivacy((prev) => ({ ...prev, [key]: value }));
    await api.put("/users/me/privacy", { [key]: value });
    toast.success("Privacy setting updated");
  };

  const updateNotifSetting = async (key, value) => {
    setNotifSettings((prev) => ({ ...prev, [key]: value }));
    await api.put("/notifications/settings", { [key]: value });
    toast.success("Notification preference updated");
  };

  const saveBio = async () => {
    setSaving(true);
    try {
      await api.put("/users/me", { bio: bioText });
      setProfile((prev) => ({ ...prev, bio: bioText }));
      setEditingBio(false);
      toast.success("Bio updated");
    } finally {
      setSaving(false);
    }
  };

  const copyPublicLink = async () => {
    const url = `${window.location.origin}${profile.public_profile_path}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public profile link copied!");
    } catch (e) {
      toast.error(`Could not copy automatically — copy manually: ${url}`);
    }
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="profile-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="profile-page">
      <div className="glass-panel rounded-2xl p-8 flex flex-col sm:flex-row items-center gap-6 mb-6">
        <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center text-2xl font-heading font-bold text-white overflow-hidden border-2 border-indigo-400/30" data-testid="profile-avatar">
          {profile.picture ? <img src={profile.picture} alt={profile.name} loading="lazy" className="w-full h-full object-cover" /> : profile.name?.[0]}
        </div>
        <div className="flex-1 text-center sm:text-left">
          <h1 className="font-heading text-2xl font-semibold text-white" data-testid="profile-name">{profile.name}</h1>
          <p className="text-sm text-slate-400">{profile.email}</p>
          <span className="inline-flex items-center gap-1 mt-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-2.5 py-0.5 text-[11px] font-bold text-purple-300" data-testid="profile-level-badge">
            <Star className="w-3 h-3" /> Level {profile.level_info.level} · {profile.level_info.title}
          </span>
        </div>
        <div className="flex gap-5">
          <div className="text-center"><div className="flex items-center gap-1 text-[#FBBF24] justify-center"><Zap className="w-4 h-4" /><span className="font-heading text-lg font-bold">{profile.xp}</span></div><p className="text-[10px] text-slate-400">XP</p></div>
          <div className="text-center"><div className="flex items-center gap-1 text-indigo-400 justify-center"><Flame className="w-4 h-4" /><span className="font-heading text-lg font-bold">{profile.streak_current}</span></div><p className="text-[10px] text-slate-400">Streak</p></div>
          <div className="text-center"><div className="flex items-center gap-1 text-indigo-400 justify-center"><Trophy className="w-4 h-4" /><span className="font-heading text-lg font-bold">#{profile.rank}</span></div><p className="text-[10px] text-slate-400">Rank</p></div>
        </div>
      </div>

      {/* Bio + Public Profile */}
      <div className="glass-panel rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-heading font-semibold text-white text-sm">Bio</h2>
          {!editingBio && (
            <button onClick={() => setEditingBio(true)} data-testid="edit-bio-button" className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
              <Pencil className="w-3 h-3" /> Edit
            </button>
          )}
        </div>
        {editingBio ? (
          <div>
            <textarea
              value={bioText} onChange={(e) => setBioText(e.target.value)} rows={3} maxLength={280}
              data-testid="bio-textarea"
              placeholder="Tell the world about yourself..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <div className="flex gap-2 mt-2">
              <button onClick={saveBio} disabled={saving} data-testid="save-bio-button" className="text-xs font-semibold bg-primary hover:bg-primary/90 px-4 py-2 rounded-lg text-white">Save</button>
              <button onClick={() => { setEditingBio(false); setBioText(profile.bio || ""); }} className="text-xs font-semibold bg-white/10 hover:bg-white/15 px-4 py-2 rounded-lg text-white">Cancel</button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400">{profile.bio || "No bio added yet."}</p>
        )}
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/5">
          <span className="text-xs text-slate-400">Public shareable profile:</span>
          <button onClick={copyPublicLink} data-testid="copy-public-profile-btn" className="flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300">
            <Copy className="w-3 h-3" /> {profile.public_profile_path}
          </button>
        </div>
      </div>

      {/* Analytics */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
        <div className="glass-panel rounded-xl p-4 text-center" data-testid="analytics-completion-pct">
          <p className="font-heading text-xl font-bold text-indigo-400">{Math.round((profile.completed_count / 30) * 100)}%</p>
          <p className="text-[10px] text-slate-400 mt-1">Completion</p>
        </div>
        <div className="glass-panel rounded-xl p-4 text-center" data-testid="analytics-avg-score">
          <p className="font-heading text-xl font-bold text-emerald-400">{profile.avg_quiz_score}%</p>
          <p className="text-[10px] text-slate-400 mt-1">Avg Quiz Score</p>
        </div>
        <div className="glass-panel rounded-xl p-4 text-center">
          <p className="font-heading text-xl font-bold text-indigo-400">{profile.streak_longest}</p>
          <p className="text-[10px] text-slate-400 mt-1">Longest Streak</p>
        </div>
        <div className="glass-panel rounded-xl p-4 text-center">
          <p className="font-heading text-xl font-bold text-[#FBBF24]">{profile.coins}</p>
          <p className="text-[10px] text-slate-400 mt-1">Coins</p>
        </div>
        <div className="glass-panel rounded-xl p-4 text-center" data-testid="analytics-streak-freezes">
          <p className="font-heading text-xl font-bold text-indigo-300 flex items-center justify-center gap-1"><Snowflake className="w-4 h-4" />{profile.streak_freezes}</p>
          <p className="text-[10px] text-slate-400 mt-1">Streak Freezes</p>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="font-heading font-semibold text-white mb-4">Achievements</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {profile.achievements.map((a) => {
            const Icon = ICON_MAP[a.icon] || Award;
            return (
              <div
                key={a.id} data-testid={`achievement-badge-${a.id}`}
                className={`glass-panel rounded-xl p-4 text-center transition-transform hover:-translate-y-1 ${a.earned ? "border-[#FBBF24]/30" : "opacity-40"}`}
              >
                <Icon className={`w-7 h-7 mx-auto mb-2 ${a.earned ? "text-[#FBBF24]" : "text-slate-400"}`} />
                <p className="text-xs font-medium text-white">{a.title}</p>
                <p className="text-[10px] text-slate-400 mt-1">{a.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {privacy && (
        <div className="glass-panel rounded-2xl p-6 mb-6" data-testid="privacy-settings-section">
          <h2 className="font-heading font-semibold text-white mb-1 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-indigo-400" /> Recruiter Visibility</h2>
          <p className="text-xs text-slate-400 mb-4">Control how verified recruiters on the platform can discover and reach you.</p>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white">Show my profile in recruiter search</p>
                <p className="text-[11px] text-slate-400">Recruiters can find you by skills, XP, and completed projects.</p>
              </div>
              <Switch checked={privacy.recruiter_visible} onCheckedChange={(v) => updatePrivacy("recruiter_visible", v)} data-testid="privacy-toggle-recruiter-visible" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white">Allow recruiters to contact me</p>
                <p className="text-[11px] text-slate-400">Recruiters can send you messages through the platform.</p>
              </div>
              <Switch checked={privacy.allow_recruiter_contact} onCheckedChange={(v) => updatePrivacy("allow_recruiter_contact", v)} data-testid="privacy-toggle-allow-contact" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white">Allow resume downloads</p>
                <p className="text-[11px] text-slate-400">Recruiters can download your AI-generated resume as a PDF.</p>
              </div>
              <Switch checked={privacy.resume_downloadable} onCheckedChange={(v) => updatePrivacy("resume_downloadable", v)} data-testid="privacy-toggle-resume-downloadable" />
            </div>
          </div>
        </div>
      )}

      {notifSettings && (
        <div id="notification-settings" className="glass-panel rounded-2xl p-6 mb-6" data-testid="notification-settings-section">
          <h2 className="font-heading font-semibold text-white mb-1 flex items-center gap-2"><Bell className="w-4 h-4 text-indigo-400" /> Notification Preferences</h2>
          <p className="text-xs text-slate-400 mb-4">Choose which categories of in-app notifications you want to receive.</p>

          <div className="flex items-center justify-between gap-4 pb-4 mb-4 border-b border-white/10">
            <div className="flex items-start gap-2.5">
              <Mail className="w-4 h-4 text-indigo-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-white font-semibold">Email Notifications</p>
                <p className="text-[11px] text-slate-400">Master switch for welcome/reminder/weekly report emails. Turn off to stop all emails regardless of category.</p>
              </div>
            </div>
            <Switch checked={notifSettings.email_opt_in} onCheckedChange={(v) => updateNotifSetting("email_opt_in", v)} data-testid="notification-toggle-email-opt-in" />
          </div>

          <div className="flex items-center justify-between gap-4 pb-4 mb-4 border-b border-white/10">
            <div className="flex items-start gap-2.5">
              <BellRing className="w-4 h-4 text-indigo-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-white font-semibold">Browser Push Notifications</p>
                <p className="text-[11px] text-slate-400">Get instant alerts on this device even when the app is closed (streak reminders, duel results, achievements).</p>
              </div>
            </div>
            <Switch checked={pushEnabled} onCheckedChange={togglePush} disabled={pushBusy} data-testid="notification-toggle-push-enabled" />
          </div>

          <div className="space-y-4">
            {Object.entries(NOTIFICATION_TYPE_META).map(([key, meta]) => {
              const Icon = meta.icon;
              return (
                <div key={key} className="flex items-center justify-between gap-4">
                  <div className="flex items-start gap-2.5">
                    <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm text-white">{meta.label}</p>
                      <p className="text-[11px] text-slate-400">{meta.desc}</p>
                    </div>
                  </div>
                  <Switch
                    checked={notifSettings[key]} onCheckedChange={(v) => updateNotifSetting(key, v)}
                    data-testid={`notification-toggle-${key}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <PortfolioAnalyticsCard />

      <div className="glass-panel rounded-2xl p-6">
        <h2 className="font-heading font-semibold text-white mb-3">Certificate</h2>
        {profile.certificate ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-400 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Certified — {profile.certificate.certificate_id}</p>
              <p className="text-xs text-slate-400 mt-1">Score: {profile.certificate.score}%</p>
            </div>
            <Link to="/certificate" data-testid="view-certificate-link" className="text-sm font-semibold text-indigo-400 hover:text-indigo-300">View Certificate →</Link>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Lock className="w-4 h-4" /> Complete all 30 days with 80%+ average score to earn your certificate.
            <Link to="/certificate" className="text-indigo-400 hover:text-indigo-300 ml-1">Check progress →</Link>
          </div>
        )}
      </div>
    </div>
  );
}
